import { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback, ReactNode } from 'react'
import { login, setAccessToken, getAccessToken, removeAccessToken } from '../api/auth'
import { LoadingSpinner } from '../components/ui/loading-spinner'
import { requestWriteAccessIfNeeded } from './requestWriteAccess'
import {
  getLaunchParamsFromHash,
  isTelegramLaunch,
  resolveInitData,
  type LaunchParams,
} from '../lib/launch-params'

const AUTH_TIMEOUT_MS = 15_000

/**
 * Конвертирует hex-цвет (#rrggbb) в HSL-компоненты "H S% L%"
 * для совместимости с CSS-переменными shadcn/ui
 */
function hexToHSL(hex: string): string {
  hex = hex.replace('#', '')
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/**
 * Применяет Telegram-тему, конвертируя hex-цвета в HSL для shadcn/ui
 */
function applyTelegramTheme(themeParams: Record<string, string | undefined>) {
  const root = document.documentElement
  const mapping: Record<string, string | undefined> = {
    '--background': themeParams.bg_color,
    '--foreground': themeParams.text_color,
    '--muted-foreground': themeParams.hint_color,
    '--primary': themeParams.button_color,
    '--primary-foreground': themeParams.button_text_color,
    '--card': themeParams.secondary_bg_color,
    '--card-foreground': themeParams.text_color,
    '--popover': themeParams.secondary_bg_color,
    '--popover-foreground': themeParams.text_color,
    '--accent': themeParams.secondary_bg_color,
    '--accent-foreground': themeParams.text_color,
    '--secondary': themeParams.secondary_bg_color,
    '--secondary-foreground': themeParams.text_color,
  }

  for (const [cssVar, hexColor] of Object.entries(mapping)) {
    if (hexColor) {
      root.style.setProperty(cssVar, hexToHSL(hexColor))
    }
  }

  // Также задаём прямые цвета для body
  if (themeParams.bg_color) {
    document.body.style.backgroundColor = themeParams.bg_color
  }
  if (themeParams.text_color) {
    document.body.style.color = themeParams.text_color
  }
}

interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  language_code?: string
  is_premium?: boolean
  allows_write_to_pm?: boolean
}

interface TelegramWebApp {
  initData: string
  initDataUnsafe: {
    user?: TelegramUser
    start_param?: string
  }
  version?: string
  platform?: string
  colorScheme: 'light' | 'dark'
  themeParams: {
    bg_color?: string
    text_color?: string
    hint_color?: string
    link_color?: string
    button_color?: string
    button_text_color?: string
    secondary_bg_color?: string
  }
  isExpanded: boolean
  viewportHeight: number
  viewportStableHeight: number
  expand: () => void
  close: () => void
  ready: () => void
  MainButton: {
    text: string
    color: string
    textColor: string
    isVisible: boolean
    isActive: boolean
    isProgressVisible: boolean
    setText: (text: string) => void
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
    show: () => void
    hide: () => void
    enable: () => void
    disable: () => void
    showProgress: (leaveActive?: boolean) => void
    hideProgress: () => void
  }
  BackButton: {
    isVisible: boolean
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
    show: () => void
    hide: () => void
  }
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void
    selectionChanged: () => void
  }
  showAlert: (message: string, callback?: () => void) => void
  showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void
  openTelegramLink: (url: string) => void
  requestWriteAccess?: (callback?: (granted: boolean) => void) => void
  shareUrl?: (params: { url: string; text?: string }) => void
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp
    }
    __reportClientError?: (message: string, stack?: string, source?: string) => void
  }
}

interface TelegramContextType {
  webApp: TelegramWebApp | null
  user: TelegramUser | null
  initData: string
  startParam: string | null
  isDarkMode: boolean
  isReady: boolean
  isAuthorized: boolean
}

const TelegramContext = createContext<TelegramContextType>({
  webApp: null,
  user: null,
  initData: '',
  startParam: null,
  isDarkMode: false,
  isReady: false,
  isAuthorized: false,
})

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [hashLaunch] = useState<LaunchParams>(() => getLaunchParamsFromHash())
  // Tracks the current auth request to drop stale concurrent responses
  const authRequestIdRef = useRef(0)
  const writeAccessRequestedRef = useRef(false)

  const reportAuthError = useCallback((message: string, stack?: string) => {
    if (typeof window.__reportClientError === 'function') {
      window.__reportClientError(message, stack, 'auth')
    }
  }, [])

  const authorizeUser = useCallback(async (initData: string, hasTelegram: boolean) => {
    const requestId = ++authRequestIdRef.current
    setAuthError(null)

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Превышено время ожидания сервера')), AUTH_TIMEOUT_MS)
    )

    try {
      if (hasTelegram) {
        if (!initData) {
          throw new Error('Нет данных Telegram (initData пуст)')
        }
        // В Telegram — ВСЕГДА логинимся заново, т.к. у разных юзеров общий WebView/localStorage
        console.log('🔐 Telegram login with fresh initData...')
        const response = await Promise.race([login(initData), timeoutPromise])
        if (authRequestIdRef.current !== requestId) return
        setAccessToken(response.accessToken)
        console.log('✅ Authorized:', response.user.username || response.user.telegramId)
      } else {
        // Dev-режим без Telegram — используем существующий токен
        const existingToken = getAccessToken()
        if (existingToken) {
          console.log('🎫 Dev mode: using existing token')
        } else {
          console.log('🔐 Dev mode: logging in without initData...')
          const response = await Promise.race([login(initData), timeoutPromise])
          if (authRequestIdRef.current !== requestId) return
          setAccessToken(response.accessToken)
          console.log('✅ Dev login OK:', response.user.telegramId)
        }
      }

      if (authRequestIdRef.current !== requestId) return
      setIsAuthorized(true)
    } catch (error: any) {
      if (authRequestIdRef.current !== requestId) return
      console.error('❌ Authorization failed:', error)
      const msg = error?.response?.data?.message || error?.message || 'Неизвестная ошибка'
      reportAuthError(msg, error?.stack)
      setAuthError(msg)
      removeAccessToken()
      setIsAuthorized(false)
    }
  }, [reportAuthError])

  useEffect(() => {
    const tg = window.Telegram?.WebApp

    if (tg) {
      setWebApp(tg)
      tg.expand()

      if (tg.colorScheme === 'dark') {
        document.documentElement.classList.add('dark')
      }
      applyTelegramTheme(tg.themeParams as Record<string, string | undefined>)

      tg.ready()

      if (!writeAccessRequestedRef.current) {
        writeAccessRequestedRef.current = true
        requestWriteAccessIfNeeded(tg, tg.initDataUnsafe?.user)
      }

      setIsReady(true)
      authorizeUser(tg.initData || hashLaunch.initData || '', true)
    } else if (hashLaunch.initData) {
      // telegram-web-app.js не загрузился, но initData есть в location.hash
      console.warn('Telegram WebApp API unavailable, using hash initData fallback')
      setIsReady(true)
      authorizeUser(hashLaunch.initData, true)
    } else {
      console.log('Running outside Telegram WebApp')
      setIsReady(true)
      authorizeUser('', false)
    }
  }, [authorizeUser, hashLaunch.initData])

  // Re-authorize when an API call receives a 401 (token expired or invalidated)
  useEffect(() => {
    const handleAuthExpired = () => {
      const tg = window.Telegram?.WebApp
      setIsAuthorized(false)
      const initData = resolveInitData(tg?.initData)
      authorizeUser(initData, isTelegramLaunch())
    }
    window.addEventListener('auth:expired', handleAuthExpired)
    return () => window.removeEventListener('auth:expired', handleAuthExpired)
  }, [authorizeUser])

  const handleRetry = useCallback(() => {
    const tg = window.Telegram?.WebApp
    setIsAuthorized(false)
    setAuthError(null)
    const initData = resolveInitData(tg?.initData)
    authorizeUser(initData, isTelegramLaunch())
  }, [authorizeUser])

  const value = useMemo<TelegramContextType>(() => ({
    webApp,
    user: webApp?.initDataUnsafe?.user || hashLaunch.user,
    initData: webApp?.initData || hashLaunch.initData,
    startParam: webApp?.initDataUnsafe?.start_param || hashLaunch.startParam,
    isDarkMode: webApp?.colorScheme === 'dark',
    isReady,
    isAuthorized,
  }), [webApp, hashLaunch, isReady, isAuthorized])

  // Block mount until auth is resolved — prevents children from rendering before
  // a JWT is available, which eliminates the BackButton flicker and early 401s
  if (!isReady || (!isAuthorized && !authError)) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: '12px',
      }}>
        <LoadingSpinner size="lg" />
        <div style={{ fontSize: '14px', color: '#999' }}>Авторизация...</div>
      </div>
    )
  }

  if (authError && !isAuthorized) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', padding: '24px', textAlign: 'center', gap: '12px',
      }}>
        <div style={{ fontSize: '14px', color: '#666' }}>Ошибка авторизации</div>
        <div style={{ fontSize: '13px', color: '#999', maxWidth: '300px' }}>{authError}</div>
        <button
          onClick={handleRetry}
          style={{
            marginTop: '8px', padding: '8px 24px', borderRadius: '8px',
            border: '1px solid #ddd', background: 'transparent', cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Повторить
        </button>
      </div>
    )
  }

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  )
}

export function useTelegram() {
  const context = useContext(TelegramContext)
  if (!context) {
    throw new Error('useTelegram must be used within TelegramProvider')
  }
  return context
}

/**
 * Получает мажорную и минорную версию Telegram WebApp API
 * @param webApp - Экземпляр TelegramWebApp или null
 * @returns Объект с major и minor версиями, или null если версия недоступна
 */
export function getApiVersion(webApp: TelegramWebApp | null): { major: number; minor: number } | null {
  if (!webApp?.version) {
    return null
  }

  // Парсим версию (формат: "6.0", "6.1", "7.0" и т.д.)
  const versionParts = webApp.version.split('.').map(Number)
  const major = versionParts[0] || 0
  const minor = versionParts[1] || 0

  return { major, minor }
}

// Hook for MainButton
// onClick stored in a ref so the effect never re-runs due to callback identity changes
export function useMainButton(text: string, onClick: () => void, visible = true) {
  const { webApp } = useTelegram()
  const onClickRef = useRef(onClick)
  onClickRef.current = onClick

  useEffect(() => {
    if (!webApp) return

    const button = webApp.MainButton
    const handler = () => onClickRef.current()

    button.setText(text)
    button.onClick(handler)

    if (visible) {
      button.show()
    } else {
      button.hide()
    }

    return () => {
      button.offClick(handler)
      button.hide()
    }
  }, [webApp, text, visible])
}

// Hook for BackButton
// onClick stored in a ref so the effect never re-runs due to callback identity changes,
// which was causing show/hide flicker on every parent re-render
export function useBackButton(onClick: () => void, visible = true) {
  const { webApp } = useTelegram()
  const onClickRef = useRef(onClick)
  onClickRef.current = onClick

  useEffect(() => {
    if (!webApp) return
    if (!webApp.BackButton) return

    const version = getApiVersion(webApp)
    if (version) {
      const { major, minor } = version
      if (major < 6 || (major === 6 && minor < 1)) return
    }

    const button = webApp.BackButton
    const handler = () => onClickRef.current()

    button.onClick(handler)

    if (visible) {
      button.show()
    } else {
      button.hide()
    }

    return () => {
      button.offClick(handler)
      button.hide()
    }
  }, [webApp, visible])
}

// Hook for haptic feedback
// Returns a stable object so callers can safely include it in useEffect deps
export function useHaptic() {
  const { webApp } = useTelegram()

  return useMemo(() => ({
    impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
      webApp?.HapticFeedback?.impactOccurred(style)
    },
    notification: (type: 'error' | 'success' | 'warning') => {
      webApp?.HapticFeedback?.notificationOccurred(type)
    },
    selection: () => {
      webApp?.HapticFeedback?.selectionChanged()
    },
  }), [webApp])
}
